/**
 * forecast-workbook.ts — Phase 05.3 Plan 06; the BROWSER writer since
 * quick-260922-mvc.
 *
 * The `.xlsx` the Combined Revenue Forecast tab's Download button produces
 * (D-14). The sheet model — palette, cells, the `Total Commit` that ties to the
 * page — now lives in `@saledash/shared/forecast/workbook`, shared with the
 * sync-worker's weekly archive job so both writers produce the same file. This
 * module only turns those sheets into bytes with `write-excel-file/browser`.
 *
 * WHY GENERATION IS CLIENT-SIDE FOR THE BUTTON (D-03, T-05.3-06-03): the
 * viewer's Upside % goes in as an ARGUMENT, never a request param. The weekly
 * archive is the one server-side copy, built at the stored default (50).
 *
 * WHY THE BROWSER ENTRY POINT: `write-excel-file/node` pulls `node:fs` and
 * `node:stream`, which the SPA's Rollup browser bundle cannot resolve.
 */
import {
  FORECAST_WORKBOOK_FILENAME,
  buildWorkbook,
  type ForecastWorkbookPeriod,
} from '@saledash/shared/forecast/workbook';
import writeXlsxFile from 'write-excel-file/browser';

import type { UpsidePct } from '@saledash/shared/forecast/weighting';

export {
  FORECAST_WORKBOOK_FILENAME,
  FORECAST_WORKBOOK_PALETTE,
  buildWorkbook,
  type ForecastWorkbookPeriod,
  type ForecastWorkbookSheet,
} from '@saledash/shared/forecast/workbook';

/** The shared cell shape is a structural subset of the writer's `SheetData`. */
function asSheetData(cq: ForecastWorkbookPeriod, nq: ForecastWorkbookPeriod, upsidePct: UpsidePct) {
  return buildWorkbook(cq, nq, upsidePct).map((s) => ({
    sheet: s.sheet,
    data: s.data,
    ...(s.columns ? { columns: s.columns } : {}),
  }));
}

/**
 * The bytes, as a `Blob`. Kept separate from `downloadWorkbook` so the suite
 * can read an `.xlsx` back headlessly.
 */
export async function workbookBlob(
  cq: ForecastWorkbookPeriod,
  nq: ForecastWorkbookPeriod,
  upsidePct: UpsidePct,
): Promise<Blob> {
  return writeXlsxFile(asSheetData(cq, nq, upsidePct)).toBlob();
}

/** Trigger the browser save under the module's constant filename. */
export async function downloadWorkbook(
  cq: ForecastWorkbookPeriod,
  nq: ForecastWorkbookPeriod,
  upsidePct: UpsidePct,
): Promise<void> {
  await writeXlsxFile(asSheetData(cq, nq, upsidePct)).toFile(FORECAST_WORKBOOK_FILENAME);
}
